'use client';

import type { PostFiltersProps } from '@routeroyal/theme-core';

/**
 * AllysPostFilters - Allys-themed post filters
 * Category and sort controls with Allys styling
 */
export function AllysPostFilters({
  categories,
  selectedCategory,
  onCategoryChange,
  tags,
  selectedTags = [],
  onTagsChange,
  sortOptions,
  sortBy,
  onSortChange,
  className = '',
  messages,
}: PostFiltersProps) {
  // Default sort options
  const defaultSortOptions = [
    { label: messages?.sortLatest || 'Latest', value: 'latest' },
    { label: messages?.sortOldest || 'Oldest', value: 'oldest' },
    { label: messages?.sortPopular || 'Popular', value: 'popular' },
  ];

  const activeSortOptions = sortOptions || defaultSortOptions;

  const handleTagClick = (tagSlug: string) => {
    if (!onTagsChange) return;
    
    if (selectedTags.includes(tagSlug)) {
      onTagsChange(selectedTags.filter((t) => t !== tagSlug));
    } else {
      onTagsChange([...selectedTags, tagSlug]);
    }
  };

  return (
    <div className={`[font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif] ${className}`}>
      {/* Category and Sort Row */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        {/* Categories */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-[#243462]">
            {messages?.filterByCategory || 'Category'}:
          </span>
          <button
            onClick={() => onCategoryChange(undefined)}
            className={`
              px-4 py-2 rounded-full text-sm font-medium
              transition-all duration-300
              ${
                !selectedCategory
                  ? 'bg-[#243462] text-white shadow-lg'
                  : 'bg-gray-100 text-[#243462] hover:bg-[#BBA339]/10 hover:text-[#BBA339]'
              }
            `}
          >
            {messages?.allCategories || 'All'}
          </button>
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => onCategoryChange(category.slug)}
              className={`
                px-4 py-2 rounded-full text-sm font-medium
                transition-all duration-300
                ${
                  selectedCategory === category.slug
                    ? 'bg-[#243462] text-white shadow-lg'
                    : 'bg-gray-100 text-[#243462] hover:bg-[#BBA339]/10 hover:text-[#BBA339]'
                }
              `}
            >
              {category.name}
              {category.postCount !== undefined && (
                <span className="ml-1.5 opacity-70">({category.postCount})</span>
              )}
            </button>
          ))}
        </div>

        {/* Sort */}
        {onSortChange && (
          <div className="flex items-center gap-2">
            <label htmlFor="post-sort-select" className="text-sm font-medium text-[#243462]">
              {messages?.sortBy || 'Sort'}:
            </label>
            <select
              id="post-sort-select"
              value={sortBy || 'latest'}
              onChange={(e) => onSortChange(e.target.value)}
              className="
                px-4 py-2 rounded-lg text-sm font-medium
                border-2 border-gray-200 bg-white text-[#243462]
                focus:ring-0 focus:border-[#BBA339]
                cursor-pointer
                transition-colors duration-300
                hover:border-[#BBA339]/50
              "
            >
              {activeSortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Tags Row (optional) */}
      {tags && tags.length > 0 && onTagsChange && (
        <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-gray-200">
          <span className="text-sm font-medium text-[#243462]">
            {messages?.filterByTag || 'Tags'}:
          </span>
          {tags.map((tag) => (
            <button
              key={tag.id}
              onClick={() => handleTagClick(tag.slug)}
              className={`
                px-3 py-1.5 rounded text-xs font-medium
                transition-all duration-300
                ${
                  selectedTags.includes(tag.slug)
                    ? 'bg-[#BBA339]/20 text-[#243462] border-2 border-[#BBA339]'
                    : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-[#BBA339]/10 hover:text-[#243462]'
                }
              `}
            >
              #{tag.name}
              {tag.postCount !== undefined && (
                <span className="ml-1 opacity-70">({tag.postCount})</span>
              )}
            </button>
          ))}
          {selectedTags.length > 0 && (
            <button
              onClick={() => onTagsChange([])}
              className="text-xs text-[#BBA339] hover:text-[#243462] underline ml-2 font-medium"
            >
              Clear tags
            </button>
          )}
        </div>
      )}
    </div>
  );
}
